import { createAsyncThunk } from '@reduxjs/toolkit';

import { PLAN_TYPE } from '@/const/plans';
import paymentModel from '@/models/payment';
import schemas from '@/validations';
import ResponseError from '@/errors/responseError';
import { getPriceById } from '@/utils/price';
import { storage } from '@/utils/browser';

import { fetchBillingInfo } from '../auth/thunks';
import { STEPS } from './constants';

const USER_COUNTRY_HEADER_KEY = 'x-user-country';

export function saveCheckoutData({ priceId, step, paymentHandlerId }) {
	storage.save('checkout', { priceId, step, paymentHandlerId });
}

export function getUserCurrency(user) {
	if (user?.paymentInfo?.currency) {
		return user.paymentInfo.currency;
	}
}

export const getPrices = createAsyncThunk(
	'checkout/getPrices',
	async (payload, { rejectWithValue, getState }) => {
		const {
			country: selectedCountry,
			allowSelectedCurrency,
			filterService
		} = payload;

		const { user, currency: selectedCurrency } = getState().auth;
		const userCurrency = getUserCurrency(user);

		const country =
			selectedCountry || user?.taxInformation?.country || user?.country;

		try {
			const { prices, headers = {} } = await paymentModel.getPrices({
				country,
				currency: allowSelectedCurrency
					? selectedCurrency || userCurrency
					: userCurrency,
				filterActive: true,
				filterService
			});

			const userCountry =
				country || headers[USER_COUNTRY_HEADER_KEY]?.toLowerCase() || null;

			const filteredPrices = prices.filter(
				item => ![PLAN_TYPE.SERVICE, PLAN_TYPE.STORAGE].includes(item.name)
			);

			const priceCurrency = prices.find(price => price.active)?.currency;

			return { prices: filteredPrices, userCountry, priceCurrency };
		} catch (error) {
			if (error instanceof ResponseError) {
				return rejectWithValue({ status: error.status, data: error.data });
			}

			throw error;
		}
	}
);

export const changePlan = createAsyncThunk(
	'checkout/changePlan',
	async (payload, { rejectWithValue }) => {
		try {
			const { priceId } = payload;

			return { priceId };
		} catch (error) {
			if (error instanceof ResponseError) {
				return rejectWithValue({ status: error.status, data: error.data });
			}

			throw error;
		}
	}
);

export const initCheckout = createAsyncThunk(
	'checkout/initCheckout',
	async (_, { rejectWithValue, dispatch, getState }) => {
		try {
			const state = getState();

			const userState = state.auth;
			const checkoutState = state.checkout;

			const {
				taxInformation,
				email = '',
				country,
				canPurchaseTrial
			} = userState.user;

			let step = STEPS.BILLING;

			const checkoutData = storage.get('checkout') || {};

			const userCurrency = getUserCurrency(userState.user);

			const [{ prices, headers = {} }] = await Promise.all([
				paymentModel.getPrices({
					country: taxInformation?.country || country,
					currency: userState.currency || userCurrency,
					filterActive: false
				}),
				dispatch(fetchBillingInfo()).unwrap()
			]);

			const activePrices = prices.filter(price => price.active);

			const userCountry =
				headers[USER_COUNTRY_HEADER_KEY]?.toLowerCase() || null;

			const parsedCheckoutPriceId =
				checkoutState?.priceId || checkoutData?.priceId?.toString() || null;

			let validPrice = getPriceById(parsedCheckoutPriceId, activePrices);

			if (validPrice?.name === PLAN_TYPE.TRIAL && !canPurchaseTrial) {
				validPrice = activePrices.find(item => item.main);
			}

			if (!validPrice?.handlerId || !validPrice?.active) {
				const validInactivePrice = getPriceById(
					parsedCheckoutPriceId,
					prices
				);

				validPrice = activePrices.find(
					price => price.name === validInactivePrice?.name
				);
			}

			if (validPrice) {
				step = schemas
					.taxInformation({ sync: true })
					.isValidSync(taxInformation)
					? STEPS.PAYMENT
					: STEPS.BILLING;
			}

			const defaultPlan = activePrices.find(price => {
				if (canPurchaseTrial) {
					return price.name === PLAN_TYPE.TRIAL;
				}

				return price.main;
			});

			const priceId = validPrice?.handlerId || defaultPlan?.handlerId;

			const priceCurrency = prices.find(price => price.active)?.currency;

			let relevantBillingData;

			if (taxInformation) {
				relevantBillingData = {
					country: taxInformation?.country || '',
					accountType: taxInformation?.type || '',
					email: taxInformation?.email || email || ''
				};
			}

			return {
				priceId,
				paymentHandlerId: checkoutData.paymentHandlerId,
				userCountry,
				activePrices,
				step,
				relevantBillingData,
				priceCurrency
			};
		} catch (error) {
			if (error instanceof ResponseError) {
				return rejectWithValue({ status: error.status, data: error.data });
			}

			throw error;
		}
	}
);

export const fetchPrices = createAsyncThunk(
	'checkout/fetchPrices',
	async (
		{ country, currency, filterActive, filterService },
		{ rejectWithValue }
	) => {
		try {
			return await paymentModel.getPrices({
				country,
				currency,
				filterActive,
				filterService
			});
		} catch (error) {
			if (error instanceof ResponseError) {
				return rejectWithValue({ status: error.status, data: error.data });
			}

			throw error;
		}
	}
);
